import React, { useState, useEffect } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import itemLogo from '../assets/icons/icon-gas-truck.svg';

import './fuelEstimateCard.css';

const VEHICLE_TYPE = 'VEHICLE';

// Componente de Vehículo que se puede arrastrar
const VehicleItem = ({ index, vehicle, fuelPerHour, indexWidth }) => {
    const [, ref] = useDrag({
        type: VEHICLE_TYPE,
        item: { vehicle },
    });

    const renderDataItem = (value, previousValue, formatter, unit) => (
        <span className={`${value > previousValue ? 'high' : (value < previousValue ? 'low' : 'normal')}`} style={{ textAlign: 'center' }}>
            {formatter(value)} {unit}
        </span>
    );

    const formatTime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h}h ${m}m ${s}s`;
    };

    const calculateFuelLoss = (idleTimeInSeconds) => {
        const idleTimeInHours = idleTimeInSeconds / 3600; // Convertir segundos a horas
        return idleTimeInHours * fuelPerHour;
    };

    return (
        // <div ref={ref} className='vehicle-item'>
        //     <span>{vehicle.label}</span>
        //     <span>{calculateFuelLoss(vehicle.currentIdle).toFixed(2)} L</span>
        //     <span>{formatTime(vehicle.currentIdle)}</span>
        // </div>
        <div ref={ref} className='itemTop_container' style={{ cursor: 'grab' }}>
            <span className='itemTop_index' style={{ '--item-index-width': indexWidth }}>{index}</span>
            <img src={itemLogo} alt='Ícono del vehículo' className='itemTop_logo' />
            <span className='itemTop_name'>{vehicle.label}</span>
            <div className='itemTop_data_container'>
                {renderDataItem(calculateFuelLoss(vehicle.currentIdle), calculateFuelLoss(vehicle.previousIdle), val => val.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'L')}
                {renderDataItem(vehicle.currentIdle, vehicle.previousIdle, formatTime)}
            </div>
            <div className='itemTop_data_container'>
                {renderDataItem(calculateFuelLoss(vehicle.previousIdle), calculateFuelLoss(vehicle.currentIdle), val => val.toLocaleString('en-Us', { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'L')}
                {renderDataItem(vehicle.previousIdle, vehicle.currentIdle, formatTime)}
            </div>
        </div>
    );
};

// Contenedor de columna para los vehículos de un tipo específico
const VehicleColumn = ({ vehicles, fuelPerHour, onDrop }) => {
    const [isOver, setIsOver] = useState(false);

    const [{ canDrop, isOverCurrent }, ref] = useDrop({
        accept: VEHICLE_TYPE,
        drop: (item) => onDrop(item.vehicle),
        collect: (monitor) => ({
            isOverCurrent: monitor.isOver({ shallow: true }),
            canDrop: monitor.canDrop(),
        }),
        hover: () => setIsOver(true),
        leave: () => setIsOver(false),
    });

    useEffect(() => {
        setIsOver(isOverCurrent); // Actualizar estado según monitor
    }, [isOverCurrent]);

    const indexWidth = vehicles.length < 10 ? '2ch' : vehicles.length < 100 ? '3ch' : '4ch';

    return (
        <div
            ref={ref}
            className='vehicle_container'
        >
            <div className={`${isOver ? 'hovered' : ''}`}>
                {vehicles.length === 0 ? (
                    <span className='text_no_vehicles'>Ningún vehículo agregado</span>
                ) : (
                    vehicles.map((vehicle, index) => (
                        <VehicleItem key={vehicle.label} index={index + 1} vehicle={vehicle} fuelPerHour={fuelPerHour} indexWidth={indexWidth} />
                    ))
                )}
            </div>
        </div>
    );
};

// Componente principal de la tarjeta de estimación de combustible perdido
const FuelEstimateCard = ({ devices, displayedTopRalentiFuel, showAllTopRalentiFuel, userConstants, updateUserConstants }) => {
    const [columns, setColumns] = useState({ carros: userConstants.consCars, camionetas: userConstants.consPickups, tractocamiones: userConstants.consTrucks });

    const [fuelPerHourCar, setFuelPerHourCar] = useState(userConstants.consumptionCar);
    const [fuelPerHourPickUp, setFuelPerHourPickUp] = useState(userConstants.consumptionPickup);
    const [fuelPerHourTruck, setFuelPerHourTruck] = useState(userConstants.consumptionTruck);

    const calculateFuelLoss = (idleTimeInSeconds, fuelPerHour) => {
        const idleTimeInHours = idleTimeInSeconds / 3600; // Convertir segundos a horas
        return idleTimeInHours * fuelPerHour;
    };

    // Mapear tipos de dispositivos a categorías esperadas
    const classifyVehicle = (deviceType) => {
        switch (deviceType) {
            case 'car':
                return 'carros';
            case 'truck':
                return 'camionetas';
            case 'tractotruck':
                return 'tractocamiones';
            default:
                return 'carros'; // Categoría predeterminada
        }
    };

    const sortVehicles = (vehicles, fuelPerHour) => {
        return vehicles.sort((a, b) => {
            const aFuelLoss = calculateFuelLoss(a.currentIdle, fuelPerHour);
            const bFuelLoss = calculateFuelLoss(b.currentIdle, fuelPerHour);
            if (bFuelLoss === aFuelLoss) {
                return calculateFuelLoss(b.previousIdle, fuelPerHour) - calculateFuelLoss(a.previousIdle, fuelPerHour);
            }
            return bFuelLoss - aFuelLoss;
        });
    };

    // Actualizar columnas al cambiar `displayedTopRalentiFuel`
    useEffect(() => {
        const currentCategoryMap = {};
        Object.keys(columns).forEach((category) => {
            columns[category].forEach((vehicle) => {
                currentCategoryMap[vehicle.label] = category;
            });
        });

        // Filtramos dispositivos visibles
        const visibleDevices = devices.filter(device => device.visible);

        const updatedColumns = visibleDevices.reduce(
            (acc, device) => {
                const fuelData = displayedTopRalentiFuel.find((item) => item.car === device.label);
                const updatedDevice = {
                    ...device,
                    currentIdle: fuelData?.currentIdle || 0,
                    previousIdle: fuelData?.previousIdle || 0,
                };

                const category = currentCategoryMap[device.label] || classifyVehicle(device.type);
                acc[category] = [...(acc[category] || []), updatedDevice];
                return acc;
            },
            { carros: [], camionetas: [], tractocamiones: [] }
        );

        Object.keys(updatedColumns).forEach((type) => {
            const fuelPerHour = type === 'car' ? fuelPerHourCar : type === 'pickup' ? fuelPerHourPickUp : fuelPerHourTruck;
            updatedColumns[type] = sortVehicles(updatedColumns[type], fuelPerHour);
        });

        updateUserConstants({consCars: updatedColumns.carros, consPickups: updatedColumns.camionetas, consTrucks: updatedColumns.tractocamiones });
        setColumns(updatedColumns);
    }, [devices, displayedTopRalentiFuel]);

    // Obtener vehículos recortados si `showAllTopRalentiFuel` es true
    const getLimitedVehicles = (vehicles) => {
        return showAllTopRalentiFuel ? vehicles : vehicles.slice(0, 5);
    };

    const moveVehicle = (vehicle, from, to) => {
        const fuelPerHour =
            to === 'carros' ? fuelPerHourCar
                : to === 'camionetas' ? fuelPerHourPickUp
                    : fuelPerHourTruck;

        setColumns((prev) => {
            const newColumns = { ...prev };
            newColumns[from] = newColumns[from].filter((v) => v.label !== vehicle.label);
            newColumns[to] = sortVehicles([...newColumns[to], vehicle], fuelPerHour);
            updateUserConstants({consCars: newColumns.carros, consPickups: newColumns.camionetas, consTrucks: newColumns.tractocamiones });
            return newColumns;
        });
    };

    const handleDrop = (vehicle, to) => {
        const from = Object.keys(columns).find(col => columns[col].some(v => v.label === vehicle.label));
        if (from && from !== to) moveVehicle(vehicle, from, to);
    };

    const handleFuelChange = (setter, minValue, type) => (e) => {
        const value = parseFloat(e.target.value);
        if (!isNaN(value) && value >= minValue) {
            updateUserConstants({[type]: value});
            setter(value);
        } else {
            updateUserConstants({[type]: minValue});
            setter(minValue); // Si no es un número válido, restablecemos al valor mínimo
        }
    };

    return (
        <DndProvider backend={HTML5Backend}>
            {/* <div className='fuel-estimate-card'> */}
                <div className='vehicle-columns'>
                    <div className='vehicle-column'>
                        <span>Carros</span>
                        <div className='increment_input_container'>
                            <label>
                                Consumo aprox. por hora (L/h):
                            </label>
                            <input
                                type='number'
                                value={fuelPerHourCar}
                                min='0.5'
                                max='2.0'
                                step='0.1'
                                onChange={handleFuelChange(setFuelPerHourCar, 0.5, 'consumptionCar')}
                            />
                        </div>
                        <VehicleColumn
                            vehicles={getLimitedVehicles(columns.carros)}
                            fuelPerHour={fuelPerHourCar}
                            onDrop={(vehicle) => handleDrop(vehicle, 'carros')}
                        />
                    </div>
                    <div className='vehicle-column'>
                        <span>Camionetas</span>
                        <div className='increment_input_container'>
                            <label>
                                Consumo aprox. por hora (L/h):
                            </label>
                            <input
                                type='number'
                                value={fuelPerHourPickUp}
                                min='0.5'
                                max='2.0'
                                step='0.1'
                                onChange={handleFuelChange(setFuelPerHourPickUp, 0.5, 'consumptionPickup')}
                            />
                        </div>
                        <VehicleColumn
                            vehicles={getLimitedVehicles(columns.camionetas)}
                            fuelPerHour={fuelPerHourPickUp}
                            onDrop={(vehicle) => handleDrop(vehicle, 'camionetas')}
                        />
                    </div>
                    <div className='vehicle-column'>
                        <span>Tractocamiones</span>
                        <div className='increment_input_container'>
                            <label>
                                Consumo aprox. por hora (L/h):
                            </label>
                            <input
                                type='number'
                                value={fuelPerHourTruck}
                                min='0.5'
                                max='2.0'
                                step='0.1'
                                onChange={handleFuelChange(setFuelPerHourTruck, 0.5, 'consumptionTruck')}
                            />
                        </div>
                        <VehicleColumn
                            vehicles={getLimitedVehicles(columns.tractocamiones)}
                            fuelPerHour={fuelPerHourTruck}
                            onDrop={(vehicle) => handleDrop(vehicle, 'tractocamiones')}
                        />
                    </div>
                </div>
            {/* </div> */}
        </DndProvider>
    );
};

export default FuelEstimateCard;
